import Html from 'node-html-parser';
import { ssrBffPost } from '../../bff-api/bff';
import { rawUrlHash } from '../../utils/api';
import { sfccGet } from '../../utils/sitegenesis';
import { isServer } from '../../utils/utils';
import { preloadAbsUrls } from './preloadAbsUrls';
import { preloadContentAssets } from './preloadContentAssets';
import { preloadCustomPreferences } from './preloadCustomPreferences';
import { preloadUrls } from './preloadUrls';
import type { Categories } from '../../bff-api/categories/model';
import type { CustomPreference } from '../../utils/api/CustomPreference';
import type { SLTServerConfig } from './SLTConfigProvider';

interface ServiceResponse {
    absUrls: string[];
    urls: string[];
    preferences: Record<CustomPreference, string | number | boolean>;
    contentAssets: Record<string, string>;
    categories: Categories;
}

const CONFIG_REFRESH_RATE = 1000 * 60 * 3; // 3 minutes

let ENV_VARS = {};
if (isServer) {
    ENV_VARS = {
        turnToSiteKey: process.env.REACT_APP_TURNTO_SITE_KEY || '',
        awsRumApplicationId: process.env.SLT_AWS_RUM_APPLICATION_ID || '',
        awsRumEnableMonitoring: process.env.SLT_AWS_RUM_ENABLE_MONITORING || false,
        awsRumGuestRoleARN: process.env.SLT_AWS_RUM_GUEST_ROLE_ARN || '',
        awsRumIdentityPoolId: process.env.SLT_AWS_RUM_IDENTITY_POOL_ID || '',
        recaptchaEnterpriseProjectId: process.env.RECAPTCHA_ENTERPRISE_PROJECT_ID || '',
        recaptchaEnterpriseApiKey: process.env.RECAPTCHA_ENTERPRISE_API_KEY || '',
        recaptchaEnterpriseV3SiteKey: process.env.RECAPTCHA_ENTERPRISE_V3_SITE_KEY || '',
        recaptchaEnterpriseV2SiteKey: process.env.RECAPTCHA_ENTERPRISE_V2_SITE_KEY || '',
    };
}

export const DEFAULT_SERVER_CONFIG: SLTServerConfig = {
    absUrlMap: {},
    customPreferences: {},
    contentAssets: {},
    contentSlots: {},
    urlMap: {},
    categories: [],
    env: ENV_VARS,
};

let serverConfigLoaded = false;
let currentConfig: SLTServerConfig | undefined;

const _getConfigs = async (): Promise<SLTServerConfig> => {
    const preLoadedSlots = await sfccGet<string>(
        'PwaContent-GetContentSlot',
        [
            {
                contentSlotId: 'pre-loaded-slots',
            },
        ],
        undefined,
        false,
    );
    const html = Html.parse(preLoadedSlots);
    const preloadedContentSlots: Record<string, string> = {};
    html.querySelectorAll('.preloaded-slot').forEach((slotNode) => {
        preloadedContentSlots[slotNode.attrs.id] = slotNode.innerHTML;
    });
    const configs = await ssrBffPost<ServiceResponse>('bridge/server-config', {
        urlList: preloadUrls,
        absUrlList: preloadAbsUrls,
        preferences: preloadCustomPreferences,
        contentAssets: preloadContentAssets,
    });

    const absUrlMap: Record<string, string> = {};
    preloadAbsUrls.forEach((item, i) => {
        absUrlMap[rawUrlHash(item)] = configs.absUrls[i];
    });

    const urlMap: Record<string, string> = {};
    preloadUrls.forEach((item, i) => {
        urlMap[rawUrlHash(item)] = configs.urls[i];
    });

    return {
        absUrlMap,
        urlMap,
        customPreferences: configs.preferences,
        contentSlots: preloadedContentSlots,
        contentAssets: configs.contentAssets,
        categories: configs.categories.map((category) => ({ ...category, subCategories: [] })),
        env: ENV_VARS,
    };
};

const _scheduleConfigUpdate = async (): Promise<SLTServerConfig | undefined> => {
    try {
        currentConfig = { ...DEFAULT_SERVER_CONFIG, ...(await _getConfigs()) };
        serverConfigLoaded = true;
    } catch (e) {
        console.error(
            'Error loading the server configs, keeping the previous value which is potentially outdated or empty',
            e,
        );
    }
    return currentConfig;
};

if (isServer) {
    // eslint-disable-next-line no-void
    void (async (): Promise<void> => {
        await _scheduleConfigUpdate();
        setInterval(_scheduleConfigUpdate, CONFIG_REFRESH_RATE);
    })();
}

export const serverConfigsRequest = async (): Promise<SLTServerConfig> => {
    if (!serverConfigLoaded) {
        await _scheduleConfigUpdate();
    }
    return currentConfig ?? DEFAULT_SERVER_CONFIG;
};

// TODO: Remove this function when `withReactQuery` hoc provides a way to seed data such as precached data like this
// 🚨 It is a hacky way to let react-ssr-prepass there is some data loaded (from currentConfig singleton)
export const serverConfigsRequestSync = (): SLTServerConfig | undefined =>
    isServer && serverConfigLoaded ? currentConfig : undefined;
